import { useEffect, useRef, useState, useCallback } from "react";
import { api, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { bolehPakaiAI } from "../lib/izinAI";
import { toast } from "sonner";
import {
  RefreshCw, CheckCircle2, AlertTriangle, Sparkles, Camera, X,
  PartyPopper, Loader2, ClipboardCheck, ImageOff, Upload,
  ShieldAlert, ThumbsUp, ThumbsDown,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { formatTanggal } from "@/lib/tanggal";

const STATUS_PO = {
  terbuka: { l: "Menunggu Kiriman", c: "bg-amber-50 text-amber-700 border-amber-200" },
  sebagian: { l: "Diterima Sebagian", c: "bg-sky-50 text-sky-700 border-sky-200" },
  selesai: { l: "Selesai", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  batal: { l: "Dibatalkan", c: "bg-slate-100 text-slate-500 border-slate-200" },
};

const fmtTanggalJam = (iso) => {
  if (!iso) return "";
  try {
    return formatTanggal(iso, { denganJam: true });
  } catch { return ""; }
};

export default function InventoryRekonPO() {
  const { user, can } = useAuth();
  const bolehUpdate = can("inventory_po", "update");

  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [jumlahTidakCocok, setJumlahTidakCocok] = useState(0);
  const [jumlahSelisihNilai, setJumlahSelisihNilai] = useState(0);
  const [includeSelesai, setIncludeSelesai] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selesaiModal, setSelesaiModal] = useState(null); // { po }
  const [catatanSelesai, setCatatanSelesai] = useState("");
  const [savingSelesai, setSavingSelesai] = useState(false);
  // busy[po_id] = "ai" | "foto" — hanya satu aksi berjalan per PO pada satu waktu
  const [busy, setBusy] = useState({});
  const [expanded, setExpanded] = useState({}); // po_id -> true kalau panel analisis dibuka

  // Poin 2 (brief owner 17 Agu #3): penerimaan yang selisih NILAI-nya vs PO
  // melewati ambang ditahan (belum ada stok/jurnal/utang) sampai disetujui —
  // daftar & aksinya terpisah dari `items` (rekon qty/nilai historis) karena
  // sumbernya koleksi lain (`inventory_penerimaan_pending_nilai`).
  const [pendingNilai, setPendingNilai] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [busyPending, setBusyPending] = useState({}); // id -> "approve" | "tolak"
  const [tolakModal, setTolakModal] = useState(null); // { item }
  const [alasanTolak, setAlasanTolak] = useState("");
  const [savingTolak, setSavingTolak] = useState(false);

  // Nomor urut request: toggle "tampilkan yang sudah dicek" beruntun tidak boleh
  // membiarkan respons lama menimpa daftar — lihat AttendanceReview.jsx:36-45.
  const reqSeq = useRef(0);
  const load = () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    api.get("/inventory/rekonsiliasi-po", { params: { include_selesai: includeSelesai } })
      .then(r => {
        if (seq !== reqSeq.current) return;
        setItems(r.data.items || []);
        setCount(r.data.count || 0);
        setJumlahTidakCocok(r.data.jumlah_po_tidak_cocok || 0);
        setJumlahSelisihNilai(r.data.jumlah_po_selisih_nilai || 0);
      })
      .catch(e => { if (seq === reqSeq.current) toast.error(e?.response?.data?.detail || "Gagal memuat data rekonsiliasi PO"); })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [includeSelesai]);

  const loadPending = useCallback(() => {
    setLoadingPending(true);
    api.get("/inventory/penerimaan-pending-nilai", { params: { status: "menunggu_approval" } })
      .then(r => setPendingNilai(r.data.items || []))
      .catch(e => toast.error(e?.response?.data?.detail || "Gagal memuat penerimaan menunggu approval nilai"))
      .finally(() => setLoadingPending(false));
  }, []);
  useEffect(() => { loadPending(); }, [loadPending]);

  const setBusyPendingFor = (id, val) => setBusyPending(b => ({ ...b, [id]: val }));

  const setujuiPending = async (item) => {
    setBusyPendingFor(item.id, "approve");
    try {
      const r = await api.post(`/inventory/penerimaan-pending-nilai/${item.id}/approve`, {});
      toast.success(r?.data?.message || `Penerimaan ${item.code} disetujui`);
      loadPending();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyetujui penerimaan");
    } finally { setBusyPendingFor(item.id, null); }
  };

  const bukaTolakModal = (item) => { setTolakModal({ item }); setAlasanTolak(""); };

  const tolakPending = async () => {
    if (!tolakModal) return;
    if (!alasanTolak.trim()) { toast.error("Alasan penolakan wajib diisi"); return; }
    setSavingTolak(true);
    try {
      const r = await api.post(`/inventory/penerimaan-pending-nilai/${tolakModal.item.id}/tolak`,
        { reason: alasanTolak.trim() });
      toast.success(r?.data?.message || `Pengajuan ${tolakModal.item.code} ditolak`);
      setTolakModal(null);
      loadPending();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menolak pengajuan");
    } finally { setSavingTolak(false); }
  };

  const setBusyFor = (poId, val) => setBusy(b => ({ ...b, [poId]: val }));
  const setItemAnalisis = (poId, analisis) => {
    setItems(list => list.map(it => it.po_id === poId
      ? { ...it, rekon_ai: { ...analisis, at: analisis.at || new Date().toISOString() } }
      : it));
    setExpanded(e => ({ ...e, [poId]: true }));
  };

  const bukaSelesaiModal = (po) => { setSelesaiModal({ po }); setCatatanSelesai(""); };

  const tandaiSelesai = async () => {
    if (!selesaiModal) return;
    setSavingSelesai(true);
    try {
      await api.patch(`/inventory/rekonsiliasi-po/${selesaiModal.po.po_id}`, { catatan: catatanSelesai });
      toast.success(`PO ${selesaiModal.po.code} ditandai sudah dicek`);
      setSelesaiModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menandai PO sebagai sudah dicek");
    } finally { setSavingSelesai(false); }
  };

  const analisisAI = async (po) => {
    setBusyFor(po.po_id, "ai");
    try {
      const { data } = await api.post(`/inventory/rekonsiliasi-po/${po.po_id}/analisis`, { foto_base64: null });
      setItemAnalisis(po.po_id, data.analisis);
      toast.success("Analisis AI selesai");
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 503
        ? (e?.response?.data?.detail || "Layanan AI belum siap — kunci API belum diatur")
        : (e?.response?.data?.detail || "Gagal menjalankan analisis AI"));
    } finally { setBusyFor(po.po_id, null); }
  };

  const cocokkanFoto = async (po, file) => {
    if (!file) return;
    setBusyFor(po.po_id, "foto");
    try {
      const fotoBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await api.post(`/inventory/rekonsiliasi-po/${po.po_id}/analisis`, { foto_base64: fotoBase64 });
      setItemAnalisis(po.po_id, data.analisis);
      toast.success("Foto nota berhasil dicocokkan");
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 503
        ? (e?.response?.data?.detail || "Layanan AI belum siap — kunci API belum diatur")
        : (e?.response?.data?.detail || "Gagal mencocokkan foto nota"));
    } finally { setBusyFor(po.po_id, null); }
  };

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="inventory-rekon-po">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Inventory</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">
            Rekonsiliasi PO/DO
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Bandingkan pesanan (PO) dengan barang yang benar-benar diterima (DO) — QTY dan NILAI — temukan selisih sebelum ditutup.
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Setiap PO bisa dicocokkan otomatis dengan foto nota supplier memakai AI — klik <span className="font-medium text-slate-700">Upload Foto Nota</span> pada kartu PO.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} disabled={loading} data-testid="btn-rekon-refresh"
                  className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Muat Ulang
          </button>
          <label className="h-10 px-4 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 inline-flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={includeSelesai} onChange={e => setIncludeSelesai(e.target.checked)}
                   data-testid="toggle-rekon-selesai" className="w-4 h-4 rounded border-slate-300 accent-emerald-600" />
            Tampilkan yang sudah dicek
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kartu label="PO dengan Selisih Qty" nilai={jumlahTidakCocok} sub="qty diterima ≠ qty PO" tone="text-rose-700" />
        <Kartu label="PO dengan Selisih Nilai" nilai={jumlahSelisihNilai} sub="harga diterima ≠ harga PO (walau qty cocok)" tone="text-amber-700" />
        <Kartu label="PO Ditampilkan" nilai={count} sub={includeSelesai ? "termasuk yang sudah dicek" : "belum dicek saja"} />
      </div>

      {/* Poin 2 (brief owner 17 Agu #3): penerimaan yang selisih nilainya
          melewati ambang ditahan — belum ada stok/jurnal/utang sampai SPV
          menyetujui/menolak di sini. */}
      {!loadingPending && pendingNilai.length > 0 && (
        <section className="bg-amber-50 border border-amber-200 rounded-xl shadow-sm p-4 md:p-5" data-testid="rekon-pending-nilai-section">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-5 h-5 text-amber-700" />
            <h2 className="font-display font-bold text-slate-900 text-sm md:text-base">
              Menunggu Approval Selisih Nilai ({pendingNilai.length})
            </h2>
          </div>
          <p className="text-xs text-slate-600 mb-3">
            Penerimaan berikut belum tercatat sama sekali (belum ada stok, jurnal, atau utang) karena
            selisih nilainya terhadap PO melewati ambang. Setujui setelah dikonfirmasi ke supplier, atau tolak
            supaya PO-nya bisa diterima ulang dengan harga yang benar.
          </p>
          <div className="space-y-2">
            {pendingNilai.map(p => (
              <div key={p.id} data-testid={`rekon-pending-nilai-${p.id}`}
                   className="bg-white border border-amber-200 rounded-lg p-3 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">
                    <span className="font-mono text-[11px] text-slate-500">{p.code}</span> — PO {p.po_code} · {p.supplier_nama}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {p.tanggal} · diajukan oleh {p.diajukan_by_name || p.diajukan_by}
                  </p>
                  <p className="text-xs mt-1">
                    <span className="text-slate-600">Nilai PO </span>
                    <span className="font-mono">{idr(p.nilai_po_seharusnya)}</span>
                    <span className="text-slate-600"> → Diterima </span>
                    <span className="font-mono">{idr(p.nilai_diterima_murni)}</span>
                    <span className={`ml-2 font-semibold ${p.selisih_nilai >= 0 ? "text-rose-700" : "text-sky-700"}`}>
                      ({p.selisih_nilai >= 0 ? "+" : ""}{idr(p.selisih_nilai)} · {p.selisih_persen}%)
                    </span>
                  </p>
                </div>
                {bolehUpdate && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setujuiPending(p)} disabled={!!busyPending[p.id]}
                            data-testid={`btn-pending-nilai-setuju-${p.id}`}
                            className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                      {busyPending[p.id] === "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />}
                      Setujui
                    </button>
                    <button onClick={() => bukaTolakModal(p)} disabled={!!busyPending[p.id]}
                            data-testid={`btn-pending-nilai-tolak-${p.id}`}
                            className="h-9 px-3 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                      <ThumbsDown className="w-3.5 h-3.5" /> Tolak
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {loading ? (
        <LoadingState loading data-testid="rekon-po-loading" />
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid="rekon-empty">
          <PartyPopper className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-slate-700 font-medium">Semua PO cocok 🎉</p>
          <p className="text-sm text-slate-500 mt-1">Tidak ada selisih qty maupun nilai PO vs yang diterima yang perlu dicek saat ini.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(po => (
            <KartuPO key={po.po_id} po={po} bolehUpdate={bolehUpdate} busyAksi={busy[po.po_id]}
                     bolehAI={bolehPakaiAI(user, can)}
                     terbuka={!!expanded[po.po_id]}
                     onToggle={() => setExpanded(e => ({ ...e, [po.po_id]: !e[po.po_id] }))}
                     onSelesai={() => bukaSelesaiModal(po)}
                     onAnalisisAI={() => analisisAI(po)}
                     onFoto={(file) => cocokkanFoto(po, file)} />
          ))}
        </div>
      )}

      {selesaiModal && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => !savingSelesai && setSelesaiModal(null)}>
          <div onClick={e => e.stopPropagation()} data-testid="rekon-selesai-modal"
               className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-display font-bold text-lg flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-emerald-600" /> Tandai Sudah Dicek
              </h3>
              <button onClick={() => setSelesaiModal(null)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600">
                PO <span className="font-mono">{selesaiModal.po.code}</span> — {selesaiModal.po.supplier_nama}
              </p>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Catatan (opsional)</label>
                <textarea value={catatanSelesai} onChange={e => setCatatanSelesai(e.target.value)}
                          placeholder="Contoh: selisih sudah dikonfirmasi ke supplier, akan dikirim susulan"
                          rows={3} data-testid="rekon-selesai-catatan"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              </div>
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
              <button onClick={() => setSelesaiModal(null)} disabled={savingSelesai}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
              <button onClick={tandaiSelesai} disabled={savingSelesai} data-testid="rekon-selesai-modal-submit"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {savingSelesai ? "Menyimpan..." : "Tandai Selesai"}
              </button>
            </div>
          </div>
        </div>
      )}

      {tolakModal && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => !savingTolak && setTolakModal(null)}>
          <div onClick={e => e.stopPropagation()} data-testid="rekon-tolak-nilai-modal"
               className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-display font-bold text-lg flex items-center gap-2">
                <ThumbsDown className="w-5 h-5 text-rose-600" /> Tolak Penerimaan
              </h3>
              <button onClick={() => setTolakModal(null)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600">
                Pengajuan <span className="font-mono">{tolakModal.item.code}</span> — PO {tolakModal.item.po_code}.
                Tidak ada stok/jurnal/utang yang terbentuk; PO tetap terbuka untuk diterima ulang.
              </p>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Alasan penolakan (wajib)</label>
                <textarea value={alasanTolak} onChange={e => setAlasanTolak(e.target.value)}
                          placeholder="Contoh: harga tidak wajar, cek ulang ke supplier"
                          rows={3} data-testid="rekon-tolak-nilai-alasan"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              </div>
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
              <button onClick={() => setTolakModal(null)} disabled={savingTolak}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
              <button onClick={tolakPending} disabled={savingTolak} data-testid="rekon-tolak-nilai-submit"
                      className="h-10 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium disabled:opacity-50">
                {savingTolak ? "Menyimpan..." : "Tolak Pengajuan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kartu({ label, nilai, sub, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-display text-xl font-bold mt-1.5 ${tone || "text-slate-900"}`}>{nilai}</p>
      <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>
    </div>
  );
}

// `bolehAI` dioper sebagai prop, bukan dibaca ulang lewat useAuth() di sini:
// gerbang fitur AI (brief owner 30 Agu 2026) memakai kunci `ai_assistant` yang
// SAMA dengan backend, dan menaruhnya di satu tempat di komponen halaman
// membuat "tombol AI mana saja yang bergantung izin itu" terbaca sekali pandang.
function KartuPO({ po, bolehUpdate, bolehAI, busyAksi, terbuka, onToggle, onSelesai, onAnalisisAI, onFoto }) {
  const st = STATUS_PO[po.status] || STATUS_PO.terbuka;
  const sudahDicek = po.rekon_status === "selesai_dicek";
  const sudahFoto = Array.isArray(po.rekon_ai?.baris_foto) && po.rekon_ai.baris_foto.length > 0;
  const fileRef = useRef(null);

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid={`rekon-po-${po.po_id}`}>
      <div className="p-4 flex flex-col lg:flex-row lg:items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-mono text-[11px] text-slate-500">{po.code}</p>
            <span className={`text-[11px] px-2 py-0.5 rounded border ${st.c}`}>{st.l}</span>
            {sudahDicek && (
              <span className="text-[11px] px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Sudah dicek
              </span>
            )}
            {sudahFoto && (
              <span className="text-[11px] px-2 py-0.5 rounded border bg-sky-50 text-sky-700 border-sky-200 inline-flex items-center gap-1">
                <Camera className="w-3 h-3" /> Sudah dicocokkan dengan foto
              </span>
            )}
            {po.selisih_nilai_signifikan && (
              <span className="text-[11px] px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Selisih nilai {idr(po.selisih_nilai_total)}
              </span>
            )}
          </div>
          <p className="text-slate-900 font-medium mt-1">{po.supplier_nama}</p>
          <p className="text-xs text-slate-500 mt-0.5">{po.tanggal}</p>
          {sudahDicek && (
            <p className="text-xs text-slate-500 mt-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
              {po.rekon_catatan ? <span className="text-slate-700">{po.rekon_catatan}</span> : <span className="italic text-slate-400">Tanpa catatan</span>}
              <span className="block text-[10px] text-slate-400 mt-0.5">
                oleh {po.rekon_by || "-"}{po.rekon_at ? ` · ${fmtTanggalJam(po.rekon_at)}` : ""}
              </span>
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex flex-col items-end gap-1">
            <p className="text-xs text-slate-500">Qty PO vs Diterima</p>
            <p className="font-display text-lg font-bold text-slate-900">{po.qty_diterima}/{po.qty_po}</p>
            <p className="font-mono text-xs text-slate-500">
              {idr(po.nilai_diterima_murni ?? po.nilai_po)} <span className="text-slate-400">/ PO {idr(po.nilai_po)}</span>
            </p>
            {!!po.selisih_nilai_total && (
              <p className={`font-mono text-[11px] font-semibold ${po.selisih_nilai_total >= 0 ? "text-rose-700" : "text-sky-700"}`}>
                selisih {po.selisih_nilai_total >= 0 ? "+" : ""}{idr(po.selisih_nilai_total)} ({po.selisih_nilai_persen_total}%)
              </p>
            )}
          </div>
          {/* Foto nota dibaca model VISION lewat endpoint /analisis yang sama —
              jadi tombolnya ikut gerbang AI, bukan cuma tombol "Analisis AI". */}
          {bolehAI && (<>
          <button onClick={() => fileRef.current?.click()} disabled={busyAksi === "foto"} data-testid={`btn-rekon-foto-${po.po_id}`}
                  className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 shadow-sm">
            {busyAksi === "foto" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {busyAksi === "foto" ? "Mengunggah…" : "Upload Foto Nota"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                 data-testid={`rekon-foto-input-${po.po_id}`}
                 onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFoto(f); }} />
          </>)}
        </div>
      </div>

      {Array.isArray(po.baris) && po.baris.length > 0 && (
        <div className="overflow-x-auto border-t border-slate-100">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold">SKU / Nama</th>
                <th className="px-3 py-2 font-semibold text-right">Qty PO</th>
                <th className="px-3 py-2 font-semibold text-right">Qty Diterima</th>
                <th className="px-3 py-2 font-semibold text-right">Selisih Qty</th>
                <th className="px-3 py-2 font-semibold text-right">Nilai PO</th>
                <th className="px-3 py-2 font-semibold text-right">Nilai Diterima</th>
                <th className="px-3 py-2 font-semibold text-right">Selisih Nilai</th>
                <th className="px-3 py-2 font-semibold">Keterangan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {po.baris.map((b, i) => {
                const diff = (Number(b.qty_diterima) || 0) - (Number(b.qty_po) || 0);
                const selisihNilai = Number(b.selisih_nilai) || 0;
                return (
                  <tr key={`${b.sku}-${i}`} className="hover:bg-slate-50/60" data-testid={`rekon-baris-${po.po_id}-${b.sku}`}>
                    <td className="px-3 py-2">
                      <p className="font-mono text-[11px] text-slate-500">{b.sku}</p>
                      <p className="text-slate-800">{b.nama}</p>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700">{b.qty_po}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{b.qty_diterima}</td>
                    <td className="px-3 py-2 text-right">
                      {diff < 0 ? (
                        <span className="text-[11px] px-2 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200">
                          Kurang kirim ({b.selisih ?? diff})
                        </span>
                      ) : diff > 0 ? (
                        <span className="text-[11px] px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                          Lebih kirim ({b.selisih ?? diff})
                        </span>
                      ) : (
                        <span className="text-[11px] px-2 py-0.5 rounded border bg-slate-50 text-slate-500 border-slate-200">Cocok</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-slate-600">{idr(b.nilai_po)}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-slate-600">{idr(b.nilai_diterima)}</td>
                    <td className="px-3 py-2 text-right">
                      {b.selisih_nilai_signifikan ? (
                        <span className={`text-[11px] px-2 py-0.5 rounded border font-mono ${selisihNilai >= 0 ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}>
                          {selisihNilai >= 0 ? "+" : ""}{idr(selisihNilai)} ({b.selisih_nilai_persen}%)
                        </span>
                      ) : (
                        <span className="text-[11px] text-slate-400 font-mono">{idr(selisihNilai)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{b.keterangan || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
        {bolehUpdate && !sudahDicek && (
          <button onClick={onSelesai} data-testid={`btn-rekon-selesai-${po.po_id}`}
                  className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Tandai Sudah Dicek
          </button>
        )}
        {bolehAI && (
        <button onClick={onAnalisisAI} disabled={busyAksi === "ai"} data-testid={`btn-rekon-ai-${po.po_id}`}
                className="h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
          {busyAksi === "ai" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-emerald-600" />}
          {busyAksi === "ai" ? "Menganalisis…" : po.rekon_ai ? "Analisis Ulang" : "Analisis AI"}
        </button>
        )}
        {po.rekon_ai && (
          <button onClick={onToggle} data-testid={`btn-rekon-toggle-${po.po_id}`}
                  className="h-9 px-3 rounded-lg text-emerald-700 hover:bg-emerald-50 text-xs font-medium ml-auto">
            {terbuka ? "Sembunyikan hasil AI" : "Lihat hasil AI"}
          </button>
        )}
      </div>

      {po.rekon_ai && terbuka && <PanelAnalisisAI ai={po.rekon_ai} />}
    </div>
  );
}

function PanelAnalisisAI({ ai }) {
  return (
    <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3" data-testid="rekon-ai-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider inline-flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-emerald-600" /> Hasil Analisis AI
        </p>
        <p className="text-[11px] text-slate-400">
          {ai.at ? fmtTanggalJam(ai.at) : "baru saja"}{ai.model ? ` · ${ai.model}` : ""}
        </p>
      </div>

      {ai.ringkasan && <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{ai.ringkasan}</p>}

      {Array.isArray(ai.temuan) && ai.temuan.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-600 mb-1">Temuan</p>
          <ul className="space-y-1">
            {ai.temuan.map((t, i) => (
              <li key={i} className="text-sm text-slate-700 flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(ai.rekomendasi) && ai.rekomendasi.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-600 mb-1">Rekomendasi</p>
          <ul className="space-y-1">
            {ai.rekomendasi.map((r, i) => (
              <li key={i} className="text-sm text-slate-700 flex items-start gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" /> {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(ai.baris_foto) && ai.baris_foto.length > 0 && (
        <div className="overflow-x-auto">
          <p className="text-xs font-medium text-slate-600 mb-1">Kecocokan dengan Foto Nota</p>
          <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold">Nama</th>
                <th className="px-3 py-2 font-semibold text-right">Qty</th>
                <th className="px-3 py-2 font-semibold text-right">Harga</th>
                <th className="px-3 py-2 font-semibold">Cocok</th>
                <th className="px-3 py-2 font-semibold">Keterangan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ai.baris_foto.map((b, i) => (
                <tr key={i} className={!b.cocok ? "bg-rose-50/60" : undefined}>
                  <td className="px-3 py-2 text-slate-800">{b.nama}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{b.qty}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-700">{typeof b.harga === "number" ? idr(b.harga) : (b.harga || "-")}</td>
                  <td className="px-3 py-2">
                    {b.cocok ? (
                      <span className="text-[11px] px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Cocok
                      </span>
                    ) : (
                      <span className="text-[11px] px-2 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200 inline-flex items-center gap-1">
                        <ImageOff className="w-3 h-3" /> Tidak cocok
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{b.keterangan || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
